import React, { useState } from "react";
import { Card, CardBody, Col, Row, Button, Progress, Container } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import AlertModal from "../../Components/Common/AlertModal";

import { useCustomers } from "../../features/customers/hooks/useCustomers";
import { useJobProgress } from "../../features/customers/hooks/useJobProgress";
import { CustomersFilters } from "../../features/customers/components/CustomersFilters";
import { CustomersTable } from "../../features/customers/components/CustomersTable";
import { SendTemplateModal } from "../../features/customers/components/SendTemplateModal";
import { ConfirmationModal } from "../../features/customers/components/ConfirmationModal";
import { sendEmailTemplate } from "../../features/customers/api/customers.api";
import { whatsappApi } from "../../features/whatsapp/api/whatsapp-api";

const Spinner = ({ isLoading }: { isLoading: boolean }) => (
    isLoading ? (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
            }}
        >
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    ) : null
);

const getErrorText = (err: any): string => {
    if (!err) return "Ocurrió un error inesperado.";
    if (typeof err === 'string') return err;
    if (typeof err === 'object' && err.message) return err.message;
    return "Ocurrió un error inesperado.";
};

const renderErrorMessage = (error: any): React.ReactNode => {
    if (!error) return <span className="text-muted">No hay mensaje de error disponible</span>;
    if (typeof error === 'string') return <span className="text-muted">{error}</span>;
    if (typeof error === 'object') {
        return (
            <div className="text-start">
                {error.message && (
                    <div className="mb-2">
                        <strong className="text-dark">Mensaje:</strong>
                        <p className="text-muted mb-0">{error.message}</p>
                    </div>
                )}
                {Array.isArray(error.details) && error.details.length > 0 && (
                    <div className="mb-2">
                        <strong className="text-dark">Detalles:</strong>
                        <ul className="text-muted ps-3 mb-0" style={{ listStyleType: 'disc' }}>
                            {error.details.map((detail: string, index: number) => (
                                <li key={index}>{detail}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {error.code && (
                    <div className="mt-2 text-end">
                        <span className="badge bg-danger-subtle text-danger" style={{ fontSize: '10px' }}>
                            Código: {error.code}
                        </span>
                    </div>
                )}
            </div>
        );
    }
    return <span className="text-muted">{String(error)}</span>;
};

const ClientList = () => {
    document.title = "Clientes | Tren Taita Imbabura";

    const {
        list,
        total,
        isLoading,
        appliedFilters,
        filters,
        handleFilterChange,
        applyFilters,
        clearFilters,
        handlePageChange,
        forceApplyHasEmail,
    } = useCustomers();

    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [isSendModalOpen, setIsSendModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<{ id: string; name: string; channel: 'WhatsApp' | 'Correo' } | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [wasHasEmailForced, setWasHasEmailForced] = useState(false);

    const whatsappJob = useJobProgress('WhatsApp');
    const emailJob = useJobProgress('Correo');

    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<any>('');

    const handleOpenSendModal = () => setIsSendModalOpen(true);

    const handleSelectTemplate = (templateId: string, templateName: string, channel: 'WhatsApp' | 'Correo') => {
        setSelectedTemplate({ id: templateId, name: templateName, channel });
        setIsSendModalOpen(false);

        if (channel === 'Correo' && !appliedFilters.hasEmail) {
            forceApplyHasEmail(true);
            setWasHasEmailForced(true);
        } else {
            setWasHasEmailForced(false);
        }

        setIsConfirmModalOpen(true);
    };

    const handleCancelConfirm = () => {
        setIsConfirmModalOpen(false);
        setSelectedTemplate(null);
        if (wasHasEmailForced) {
            forceApplyHasEmail(false);
            setWasHasEmailForced(false);
        }
    };

    const handleConfirmSend = async () => {
        if (!selectedTemplate) return;

        setIsSending(true);
        try {
            const tplName = selectedTemplate.name;
            let criteria: any = { filters: {}, search: '' };
            if (appliedFilters.firstName) criteria.filters.firstName = appliedFilters.firstName;
            if (appliedFilters.lastName) criteria.filters.lastName = appliedFilters.lastName;
            if (appliedFilters.email) criteria.filters.email = appliedFilters.email;
            if (appliedFilters.document) criteria.filters.document = appliedFilters.document;
            if (appliedFilters.search) criteria.search = appliedFilters.search;
            if (appliedFilters.hasEmail) criteria.filters.hasEmail = true;

            if (selectedTemplate.channel === 'WhatsApp') {
                await whatsappApi.sendTemplate(selectedTemplate.id, criteria);
                toast.success("El envío masivo de plantillas de WhatsApp ha comenzado.");
            } else {
                await sendEmailTemplate(selectedTemplate.id, criteria);
                toast.success("El envío masivo de correos ha comenzado.");
            }

            // Cerrar modal primero para percepción de velocidad
            setIsConfirmModalOpen(false);
            setWasHasEmailForced(false);

            // Delay de seguridad para que el backend persista el Job
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Conectar al stream una vez que el Job existe
            if (selectedTemplate.channel === 'WhatsApp') {
                whatsappJob.reconnect({ templateName: tplName });
            } else {
                emailJob.reconnect({ templateName: tplName });
            }

            // Limpiar selección luego de reconectar
            setSelectedTemplate(null);
        } catch (error) {
            toast.error("Hubo un error al iniciar el envío masivo.");
            console.error("Error sending template:", error);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <React.Fragment>
            <Spinner isLoading={isLoading || isSending} />

            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col>
                            <Card>
                                <div className="d-flex align-items-center mt-4 mb-2">
                                    <h5 className="card-title mb-0 flex-grow-1 text-center fw-bold">
                                        CLIENTES
                                    </h5>
                                </div>
                                <CardBody>
                                    <Row>
                                    <CustomersFilters
                                        isOpen={isFiltersOpen}
                                        onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
                                        filters={filters}
                                        onChange={handleFilterChange}
                                        onApply={applyFilters}
                                        onClear={clearFilters}
                                    />

                                    <SendTemplateModal
                                        isOpen={isSendModalOpen}
                                        onToggle={() => setIsSendModalOpen(false)}
                                        onSend={handleSelectTemplate}
                                    />

                                    <ConfirmationModal
                                        isOpen={isConfirmModalOpen}
                                        onToggle={handleCancelConfirm}
                                        onConfirm={handleConfirmSend}
                                        templateName={selectedTemplate?.name || null}
                                        filters={appliedFilters}
                                        totalCustomers={total}
                                        isLoading={isSending}
                                    />

                                    <Col>
                                        <Row className="mb-3 justify-content-between">
                                            <Col xs={12} sm="auto" className="mb-2">
                                                <Button outline color="primary" onClick={() => setIsFiltersOpen(true)}>
                                                    <i className="ri-filter-2-line me-1"></i> Filtros
                                                </Button>
                                            </Col>
                                            <Col xs={12} sm="auto" className="mb-2 d-flex gap-2 flex-wrap">
                                                <Button color="primary" onClick={handleOpenSendModal}>
                                                    <i className="ri-send-plane-line me-1"></i> Enviar Plantilla
                                                </Button>
                                                <Link to="/template-history">
                                                    <Button color="secondary">
                                                        <i className="ri-history-line me-1"></i> Historial Plantillas
                                                    </Button>
                                                </Link>
                                            </Col>
                                        </Row>

                                        {(whatsappJob.status !== 'idle' || emailJob.status !== 'idle') && (
                                            <Row className="mb-3 g-3">
                                                {(() => {
                                                    const isBothActive = whatsappJob.status !== 'idle' && emailJob.status !== 'idle';
                                                    const colSize = isBothActive ? 6 : 12;

                                                    return (
                                                        <>
                                                            {/* COLUMNA PARA WHATSAPP */}
                                                            {whatsappJob.status !== 'idle' && (
                                                                <Col md={colSize}>
                                                                    <Card className="border shadow-none mb-0 h-100">
                                                                        <CardBody className="p-3 pe-5 bg-light rounded position-relative">
                                                                            <div className="d-flex align-items-center mb-3">
                                                                                <i className="ri-whatsapp-line text-success fs-20 me-2"></i>
                                                                                <h6 className="mb-0">Progreso WhatsApp</h6>
                                                                            </div>

                                                                            <button
                                                                                type="button"
                                                                                className="btn-close position-absolute"
                                                                                style={{ top: '12px', right: '12px', fontSize: '11px' }}
                                                                                onClick={whatsappJob.dismissJob}
                                                                                aria-label="Cerrar"
                                                                            ></button>

                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <span className="fw-medium text-muted">
                                                                                    Plantilla: <strong className="text-dark">{whatsappJob.templateName || "Cargando..."}</strong>
                                                                                </span>
                                                                                <span className={`fw-bold ${whatsappJob.status === 'failed' ? 'text-danger' : whatsappJob.status === 'completed' ? 'text-success' : 'text-primary'}`}>
                                                                                    {whatsappJob.status === 'completed' ? `Completado (${whatsappJob.progress}%)` : whatsappJob.status === 'processing' ? `Enviando (${whatsappJob.progress}%)` : 'Fallo'}
                                                                                </span>
                                                                            </div>

                                                                            {/* Barra de progreso */}
                                                                            {(whatsappJob.status === 'processing' || whatsappJob.status === 'completed') && (
                                                                                <Progress
                                                                                    value={whatsappJob.progress}
                                                                                    color={whatsappJob.status === 'completed' ? 'success' : 'primary'}
                                                                                    animated={whatsappJob.status === 'processing'}
                                                                                    striped={whatsappJob.status === 'processing'}
                                                                                    className="mb-2"
                                                                                    style={{ height: '8px' }}
                                                                                />
                                                                            )}

                                                                            {/* Mensaje Detallado de Éxito */}
                                                                            {whatsappJob.status === 'completed' && (
                                                                                <div className="d-flex align-items-center justify-content-between mb-0">
                                                                                    <div className="text-success small">
                                                                                        <i className="ri-checkbox-circle-fill me-1"></i>
                                                                                        <span className="fw-medium">Completado ({whatsappJob.progress}%)</span>
                                                                                        {whatsappJob.templateName && (
                                                                                            <span className="ms-1">— <strong>{whatsappJob.templateName}</strong></span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {/* Mensaje Detallado de Error */}
                                                                            {whatsappJob.status === 'failed' && (
                                                                                <div className="d-flex align-items-center justify-content-between mb-0">
                                                                                    <div className="text-danger small d-flex align-items-center flex-wrap">
                                                                                        <i className="ri-error-warning-fill me-1"></i>
                                                                                        <span className="fw-medium">Fallo:</span>
                                                                                        <span className="ms-1">{getErrorText(whatsappJob.error)}</span>
                                                                                        {whatsappJob.error && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn p-0 border-0 bg-transparent text-primary ms-2"
                                                                                                onClick={() => {
                                                                                                    setErrorMessage(whatsappJob.error);
                                                                                                    setErrorModalOpen(true);
                                                                                                }}
                                                                                                title="Ver error"
                                                                                            >
                                                                                                <i className="ri-information-line text-danger fs-5 align-middle"></i>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </CardBody>
                                                                    </Card>
                                                                </Col>
                                                            )}

                                                            {/* COLUMNA PARA CORREO */}
                                                            {emailJob.status !== 'idle' && (
                                                                <Col md={colSize}>
                                                                    <Card className="border shadow-none mb-0 h-100">
                                                                        <CardBody className="p-3 pe-5 bg-light rounded position-relative">
                                                                            <div className="d-flex align-items-center mb-3">
                                                                                <i className="ri-mail-line text-primary fs-20 me-2"></i>
                                                                                <h6 className="mb-0">Progreso Correo</h6>
                                                                            </div>

                                                                            <button
                                                                                type="button"
                                                                                className="btn-close position-absolute"
                                                                                style={{ top: '12px', right: '12px', fontSize: '11px' }}
                                                                                onClick={emailJob.dismissJob}
                                                                                aria-label="Cerrar"
                                                                            ></button>

                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                            <span className="fw-medium text-muted">
                                                                                Plantilla: <strong className="text-dark">{emailJob.templateName || "Cargando..."}</strong>
                                                                            </span>
                                                                                <span className={`fw-bold ${emailJob.status === 'failed' ? 'text-danger' : emailJob.status === 'completed' ? 'text-success' : 'text-primary'}`}>
                                                                                {emailJob.status === 'completed' ? `Completado (${emailJob.progress}%)` : emailJob.status === 'processing' ? `Enviando (${emailJob.progress}%)` : 'Fallo'}
                                                                            </span>
                                                                            </div>

                                                                            {/* Barra de progreso */}
                                                                            {(emailJob.status === 'processing' || emailJob.status === 'completed') && (
                                                                                <Progress
                                                                                    value={emailJob.progress}
                                                                                    color={emailJob.status === 'completed' ? 'success' : 'primary'}
                                                                                    animated={emailJob.status === 'processing'}
                                                                                    striped={emailJob.status === 'processing'}
                                                                                    className="mb-2"
                                                                                    style={{ height: '8px' }}
                                                                                />
                                                                            )}

                                                                            {/* Mensaje Detallado de Éxito */}
                                                                            {emailJob.status === 'completed' && (
                                                                                <div className="d-flex align-items-center justify-content-between mb-0">
                                                                                    <div className="text-success small">
                                                                                        <i className="ri-checkbox-circle-fill me-1"></i>
                                                                                        <span className="fw-medium">Completado ({emailJob.progress}%)</span>
                                                                                        {emailJob.templateName && (
                                                                                            <span className="ms-1">— <strong>{emailJob.templateName}</strong></span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {/* Mensaje Detallado de Error */}
                                                                            {emailJob.status === 'failed' && (
                                                                                <div className="d-flex align-items-center justify-content-between mb-0">
                                                                                    <div className="text-danger small d-flex align-items-center flex-wrap">
                                                                                        <i className="ri-error-warning-fill me-1"></i>
                                                                                        <span className="fw-medium">Fallo:</span>
                                                                                        <span className="ms-1">{getErrorText(emailJob.error)}</span>
                                                                                        {emailJob.error && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn p-0 border-0 bg-transparent text-primary ms-2"
                                                                                                onClick={() => {
                                                                                                    setErrorMessage(emailJob.error);
                                                                                                    setErrorModalOpen(true);
                                                                                                }}
                                                                                                title="Ver error"
                                                                                            >
                                                                                                <i className="ri-information-line text-danger fs-5 align-middle"></i>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </CardBody>
                                                                    </Card>
                                                                </Col>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </Row>
                                        )}

                                        <div className="card-body px-0">
                                            <CustomersTable
                                                data={list}
                                                total={total}
                                                onPageChange={handlePageChange}
                                            />
                                            <AlertModal
                                                isOpen={errorModalOpen}
                                                toggle={() => setErrorModalOpen(false)}
                                                title="Detalle del Error"
                                                message={renderErrorMessage(errorMessage)}
                                                type="danger"
                                                confirmText="Cerrar"
                                            />
                                            <ToastContainer closeButton={false} limit={1} />
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
        </React.Fragment>
    );
};

export default ClientList;
